/*
 * Canvas
 *
 * A Canvas contains a subset of a Project's Geometries and Features appropriate for the current selection.
 * It's a "Canvas" to represent a page of a PDF file or Map; see CanvasSource for more details.
 */
import { mergeRight, pick, tagged } from '@range.io/functional'
import CanvasSourceSelectors from 'range-editor/src/redux/persisted-state/canvas-source-slice/canvas-source-selectors.js'
import StringTypes from '../string-types.js'

// ---------------------------------------------------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------------------------------------------------

// prettier-ignore
const Canvas = tagged('Canvas', {
    id         : StringTypes.Id,
    name       : 'String',
    
    // for new (created in this browser) Canvases
    justCreated: 'Boolean?', // temporary flag for Canvases that haven't yet been uploaded; would never be true on load
    canvasSource: 'Object?' // CanvasSource
})

/*
 * Create a new Canvas merging an old one with values from changes
 * @sig update :: (Canvas, {k:v}) -> Canvas
 */
Canvas.update = (canvas, changes) => Canvas.from(mergeRight(canvas, changes))

// ---------------------------------------------------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Create a Canvas from a JSON object
 * @sig fromFirebase :: {k:v} -> Canvas
 */
Canvas.fromFirebase = o =>
    Canvas.from({
        id: o.id,
        name: o.name,
    })

Canvas.toFirebase = o => pick(['id', 'name'], o)

/*
 * Is s contained in the Canvas' name?
 * @sig matchesFullName = (Canvas, String) -> Boolean
 */
Canvas.matchesFullName = (canvas, s) => canvas.name.toLowerCase().includes(s.toLowerCase())

/*
 * Extend our prototype with more functions -- but only if we're using Redux. We don't really want this file to know
 * about Redux per se, but it's nice to have these additional functions defined here if we ARE using Redux, so there's
 * only one place to look for them.
 *
 * @see store.js for calls to extendForRedux
 */
// prettier-ignore
Canvas.extendForRedux = (getState, defineGetter) => {
    defineGetter(Canvas, 'canvasSources',     _this => CanvasSourceSelectors.canvasSourcesForCanvasId(getState(), _this.id))
    defineGetter(Canvas, 'firstCanvasSource', _this => () => _this.canvasSource || CanvasSourceSelectors.canvasSourcesForCanvasId(getState(), _this.id)?.[0])
}

export default Canvas
